import React, {useState, useCallback} from 'react'
import {ReactComponent as UploadIcon} from '../../../../assets/icons/upload.svg'
import {useAddImageApply} from '../hooks/useAddImageApply'
import {getDesignLibraryImage} from '../../../../services/ArtworkTemplateServices'
import {toaster} from '../../../../shared/PFToast'
import AddImageModal from './sidebar/AddImageModal'
import { placeNodeAtViewportCenter } from '../../utils/placeNodeAtViewportCenter'
import { EditorConfig } from '../../constants/editorConfig'
import { addNode } from '../../editorActions'

function CanvasEmptyState({runtime, draftId}) {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isDragOver, setIsDragOver] = useState(false)

    const {handlePickFile} = useAddImageApply({runtime, draftId})

    const handleSelectLibraryImage = useCallback(
        async (imageId) => {
            try {
                const {success, data, message} = await getDesignLibraryImage(imageId)
                if (!success) throw new Error(message)
                const url = data?.origin_url || data?.link
                if (url && runtime?.dispatch) {
                    const id = Date.now().toString()
                    const node = placeNodeAtViewportCenter(
                        {
                            ...EditorConfig.DEFAULT_IMAGE_NODE,
                            id,
                            content: {...EditorConfig.DEFAULT_IMAGE_NODE.content, src: url},
                            style: {...EditorConfig.DEFAULT_IMAGE_NODE.style},
                        },
                        runtime.getViewportCenter?.(),
                    )
                    runtime.dispatch(addNode(node, 0, true))
                }
            } catch (err) {
                toaster({type: 'error', message: err?.message || 'Failed to load image'})
            }
        },
        [runtime],
    )

    const handleDragOver = useCallback((e) => {
        e.preventDefault()
        setIsDragOver(true)
    }, [])

    const handleDragLeave = useCallback(() => {
        setIsDragOver(false)
    }, [])

    const handleDrop = useCallback(
        (e) => {
            e.preventDefault()
            setIsDragOver(false)
            const file = e.dataTransfer?.files?.[0]
            if (file && file.type.startsWith('image/')) {
                handlePickFile(file)
            }
        },
        [handlePickFile],
    )

    return (
        <div className="CanvasEmptyState">
            <div
                className={`CanvasEmptyState__Zone${isDragOver ? ' CanvasEmptyState__Zone--dragover' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => setIsModalOpen(true)}
                onKeyDown={(e) => e.key === 'Enter' && setIsModalOpen(true)}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <UploadIcon className="CanvasEmptyState__Icon" />
                <p className="CanvasEmptyState__Text">
                    Drag/Drop/Upload your design
                    <br />
                    (Max 3840 x 4160 px)
                </p>
            </div>

            <AddImageModal
                isOpen={isModalOpen}
                toggle={() => setIsModalOpen((v) => !v)}
                onPickFile={handlePickFile}
                onSelectLibraryImage={handleSelectLibraryImage}
            />
        </div>
    )
}

export default CanvasEmptyState
